import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Award, MapPin, ArrowRight, Check, X as XIcon, Satellite, Hammer, ShieldPlus, Download } from "lucide-react";
import SEO, { localBusinessSchema, faqSchema } from "../components/SEO";
import { SectionLabel, Stars, BeforeAfterSlider, CTASection } from "../components/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import CoatingStack3D from "../components/CoatingStack3D";
import { IMAGES, FAQS, TESTIMONIALS, LOCATIONS, BRAND } from "../data/siteData";

const TRUST_ITEMS = [
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: Award, label: "Lifetime Warranty" },
  { icon: Clock, label: "1-Day Install" },
  { icon: MapPin, label: "Phoenix Metro's Choice" },
];

const PAIN_POINTS = [
  "Cracked, stained concrete",
  "Dust that never goes away",
  "Peeling DIY epoxy kits",
  "Oil stains & tire marks",
  "Hot Arizona garages that destroy cheap coatings",
];

const PROCESS_STEPS = [
  { num: "1", icon: Satellite, title: "Get Your Free Quote", desc: "We measure your garage remotely using satellite imagery and confirm details with you — no in-person pressure, no surprise visits." },
  { num: "2", icon: Hammer, title: "Professional Installation", desc: "Our trained installers prep, coat, and seal your floor using our Tri-Bond™ & Lock™ System — the entire job finishes in one day." },
  { num: "3", icon: ShieldPlus, title: "Enjoy a Better Garage", desc: "Walk on it in hours. Park your car the next day. Backed by our lifetime limited warranty for as long as you own the home." },
];

export default function HomePage() {
  return (
    <>
      <SEO
        title="Vantyx Coatings | Premium Garage Coatings — Installed in 1 Day"
        description="Vantyx Coatings installs premium polyaspartic garage floor coatings across the Phoenix Metro Area — one-day install, lifetime warranty, free remote estimates via satellite imagery."
        path="/"
        ogImage={IMAGES.garage1}
        schemas={[localBusinessSchema(LOCATIONS.map((l) => l.city)), faqSchema(FAQS)]}
      />

      {/* HERO */}
      <section className="relative hero-gradient overflow-hidden grain" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <img
              src={BRAND.logoLight}
              alt="Vantyx Coatings logo"
              className="h-24 md:h-28 w-auto mb-8"
            />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              Premium Garage Coatings
              <br />
              <span className="text-[#028090]">Installed in 1 Day</span>
            </h1>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed mt-6 max-w-xl">
              Our proprietary multi-layer coating system delivers showroom-quality results without weeks of downtime.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-9">
              <Link to="/free-estimate" data-testid="hero-cta-button" className="bg-[#028090] text-white hover:bg-[#026c7a] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider text-center">
                {BRAND.ctaText}
              </Link>
              <a href={BRAND.phoneHref} data-testid="hero-phone-button" className="border border-white/30 text-white hover:border-[#028090] hover:text-[#028090] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider text-center">
                Call {BRAND.phone}
              </a>
            </div>
            <p className="text-gray-500 text-sm mt-6">Free remote estimates · Backed by warranty</p>
          </div>
          <div className="teal-glow">
            <BeforeAfterSlider before={IMAGES.concrete1} after={IMAGES.garage1} altBefore="bare concrete garage floor before polyaspartic coating phoenix az" altAfter="finished tri-bond polyaspartic garage floor coating phoenix az" />
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-[#0a0a0a] border-y border-white/10" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {TRUST_ITEMS.map((t) => (
            <div key={t.label} className="flex items-center justify-center gap-3">
              <t.icon size={22} className="text-[#028090]" strokeWidth={1.5} />
              <span className="text-gray-200 text-sm font-semibold tracking-wide">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TRUSTED BY HOMEOWNERS */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-12" data-testid="trusted-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <img src={IMAGES.garage1} alt="premium vantyx coatings garage floor phoenix az" loading="lazy" className="w-full aspect-[4/3] object-cover" />
          <div>
            <SectionLabel>What Sets Us Apart</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#000000] leading-[1.1]">
              Trusted by Homeowners
              <br />
              <span className="text-[#028090]">Who Care About Quality</span>
            </h2>
            <div className="space-y-5 mt-8 text-gray-600 leading-relaxed">
              <p>Most coating failures don't happen because of the material — they happen because of rushed prep, skipped steps, or crews that treat garage floors like a side job.</p>
              <p className="font-semibold text-[#000000]">We do the opposite.</p>
              <p>Our company specializes <strong>exclusively in garage floor coatings</strong>, which lets us refine our process, train our installers to a higher standard, and deliver consistently better results. From proper surface preparation to clean, precise finishes, every garage is installed using the same proven system.</p>
              <p>Homeowners choose us because we're not experimenting on their home — we're executing a system that's been installed successfully again and again.</p>
            </div>
            <Link to="/free-estimate" data-testid="trusted-cta" className="inline-block mt-9 bg-[#028090] text-white hover:bg-[#026c7a] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider">
              {BRAND.ctaText}
            </Link>
          </div>
        </div>
      </section>

      {/* PAIN POINTS — "Tired of a garage floor that looks like this?" */}
      <section className="bg-[#000000] py-24 md:py-32 px-6 md:px-12 grain relative" data-testid="pain-points-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <SectionLabel>The Old Way Is Broken</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1]">
              Tired of a Garage Floor
              <br />
              <span className="text-[#028090]">That Looks Like This?</span>
            </h2>
            <ul className="mt-8 space-y-4">
              {PAIN_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-gray-300 text-lg">
                  <XIcon size={22} className="text-red-400 mt-0.5 shrink-0" strokeWidth={2} />
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-10 border border-[#028090]/40 bg-[#028090]/10 p-6 md:p-8">
              <p className="text-[#E8B86D] text-sm uppercase tracking-[0.2em] font-semibold mb-2 italic">There's a better way</p>
              <p className="text-gray-200 leading-relaxed">Our advanced polyaspartic garage flooring system permanently seals your concrete, protects against heat and moisture, and delivers a clean, high-end finish — fast.</p>
            </div>
            <Link to="/free-estimate" data-testid="pain-points-cta" className="inline-block mt-8 bg-[#028090] text-white hover:bg-[#026c7a] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider">
              {BRAND.ctaText}
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={IMAGES.concrete1} alt="cracked stained bare concrete garage floor phoenix az" loading="lazy" className="w-full aspect-[3/4] object-cover grayscale" />
            <img src={IMAGES.concrete2} alt="peeling failed epoxy garage floor arizona" loading="lazy" className="w-full aspect-[3/4] object-cover grayscale mt-8" />
          </div>
        </div>
      </section>

      {/* PROCESS: A Simple Stress-Free Process */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-12" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel>How It Works</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#000000] leading-[1.1]">
              A Simple
              <br />
              <span className="text-[#028090]">Stress-Free Process</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-14">
            {PROCESS_STEPS.map((s) => (
              <div key={s.num} className="border border-gray-200 p-8 md:p-10 hover:-translate-y-1 hover:border-[#028090] transition-all duration-300 relative" data-testid={`process-step-${s.num}`}>
                <div className="flex items-start justify-between mb-5">
                  <s.icon size={30} className="text-[#028090]" strokeWidth={1.5} />
                  <span className="font-display text-6xl font-extrabold text-[#028090]/15 leading-none">{s.num}</span>
                </div>
                <h3 className="font-display text-xl font-bold text-[#000000] mb-3">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link to="/free-estimate" data-testid="process-cta" className="inline-block bg-[#028090] text-white hover:bg-[#026c7a] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider">
              {BRAND.ctaText}
            </Link>
          </div>
        </div>
      </section>

      {/* TRI-BOND™ & LOCK™ SYSTEM — 3D scroll-linked coating build */}
      <CoatingStack3D />

      <section className="bg-[#000000] px-6 md:px-12 pb-16" data-testid="tribond-cta">
        <div className="max-w-7xl mx-auto text-center">
          <Link to="/services" data-testid="tribond-learn-more" className="inline-flex items-center gap-2 border border-white/30 text-white hover:border-[#028090] hover:text-[#028090] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider">
            Explore the Full System <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* BEFORE / AFTER GALLERY */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-12" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <SectionLabel>Proof, Not Promises</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#000000]">Drag the slider. See the transformation.</h2>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-14">
            <BeforeAfterSlider before={IMAGES.concrete1} after={IMAGES.garage2} altBefore="stained concrete garage floor before coating scottsdale az" altAfter="dakota grey flake polyaspartic garage floor scottsdale az" />
            <BeforeAfterSlider before={IMAGES.concrete2} after={IMAGES.garage3} altBefore="cracked garage slab before tri-bond coating gilbert az" altAfter="tuxedo flake garage floor coating gilbert az" />
          </div>
          <div className="text-center mt-12">
            <Link to="/portfolio" data-testid="gallery-portfolio-link" className="inline-flex items-center gap-2 border border-[#028090] text-[#028090] hover:bg-[#028090] hover:text-white transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider">
              View Full Portfolio <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — "What others are saying" */}
      <section className="bg-[#000000] py-24 md:py-32 px-6 md:px-12" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <SectionLabel>Vantyx provides an unparalleled experience</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1]">
              What Others <span className="text-[#028090]">Are Saying</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {TESTIMONIALS.slice(0, 3).map((t) => (
              <div key={t.name} className="bg-white/5 border border-white/10 p-8 flex flex-col hover:border-[#028090]/40 transition-colors">
                <Stars count={t.stars} />
                <p className="text-gray-300 text-sm leading-relaxed mt-4 flex-1">"{t.text}"</p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="text-white font-semibold text-sm">{t.name}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{t.city}, AZ</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/reviews" data-testid="testimonials-reviews-link" className="text-[#028090] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 hover:gap-3 transition-all">
              Read All Reviews <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRICING & WARRANTY MESSAGE */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-12" data-testid="pricing-message-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <SectionLabel>Straightforward. Transparent. Guaranteed.</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#000000] leading-[1.1]">
              Straightforward Pricing.
              <br />
              Backed by a <span className="text-[#028090]">Real Warranty.</span>
            </h2>
            <div className="space-y-5 mt-8 text-gray-600 leading-relaxed">
              <p>Upgrading your garage floor should feel like a smart investment — not a gamble.</p>
              <p>That's why we use <strong className="text-[#000000]">clear, flat-rate pricing</strong> and stand behind every install with a <strong className="text-[#000000]">written warranty</strong>. No confusing quotes, no surprise add-ons, and no fine print designed to disappear when you need support.</p>
              <p>Our pricing is based on the size and condition of your garage, along with the finish you choose. Once we provide your quote, that's the price — period.</p>
            </div>
            <Link to="/free-estimate" data-testid="pricing-message-cta" className="inline-block mt-9 bg-[#028090] text-white hover:bg-[#026c7a] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider">
              {BRAND.ctaText}
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-gray-200 p-6 flex flex-col items-center text-center hover:-translate-y-1 transition-transform">
              <ShieldCheck size={32} className="text-[#028090] mb-3" strokeWidth={1.5} />
              <p className="font-display font-bold text-[#000000]">Licensed &amp; Insured</p>
              <p className="text-gray-500 text-xs mt-1">Arizona-based</p>
            </div>
            <div className="border border-gray-200 p-6 flex flex-col items-center text-center hover:-translate-y-1 transition-transform mt-6">
              <Award size={32} className="text-[#E8B86D] mb-3" strokeWidth={1.5} />
              <p className="font-display font-bold text-[#000000]">Lifetime Warranty</p>
              <p className="text-gray-500 text-xs mt-1">In writing</p>
            </div>
            <div className="border border-gray-200 p-6 flex flex-col items-center text-center hover:-translate-y-1 transition-transform">
              <Clock size={32} className="text-[#028090] mb-3" strokeWidth={1.5} />
              <p className="font-display font-bold text-[#000000]">1-Day Install</p>
              <p className="text-gray-500 text-xs mt-1">Drive on in 24 hrs</p>
            </div>
            <div className="border border-gray-200 p-6 flex flex-col items-center text-center hover:-translate-y-1 transition-transform mt-6">
              <Check size={32} className="text-[#028090] mb-3" strokeWidth={1.5} />
              <p className="font-display font-bold text-[#000000]">Flat-Rate Pricing</p>
              <p className="text-gray-500 text-xs mt-1">Zero surprises</p>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-12 flex flex-wrap justify-center items-center gap-4">
          <Link
            to="/warranty"
            data-testid="warranty-badge-cta"
            className="group inline-flex items-center gap-3 bg-[#000000] border-2 border-[#E8B86D] text-white hover:bg-[#E8B86D] hover:text-[#000000] transition-all duration-300 pl-5 pr-6 py-3 rounded-full font-display font-bold uppercase tracking-wider text-sm shadow-lg"
          >
            <Award size={20} strokeWidth={2} className="text-[#E8B86D] group-hover:text-[#000000] transition-colors" />
            Lifetime Warranty — See Details
            <ArrowRight size={16} strokeWidth={2.5} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href="/vantyx-lifetime-warranty.pdf"
            download="Vantyx-Lifetime-Limited-Warranty.pdf"
            data-testid="warranty-pdf-home"
            className="inline-flex items-center gap-2 text-[#028090] hover:text-[#000000] transition-colors duration-200 px-3 py-2 font-display font-bold uppercase tracking-wider text-xs underline underline-offset-4 decoration-2"
          >
            <Download size={15} strokeWidth={2.5} /> Download PDF
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50 py-24 md:py-32 px-6 md:px-12" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <SectionLabel>Questions, Answered</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#000000]">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display font-semibold text-[#000000] hover:text-[#028090]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA WITH FORM */}
      <CTASection showForm title="Get Your Free Garage Quote" subtitle={`Free remote estimate using satellite imagery. Flat-rate written pricing. One-day install. Call ${BRAND.phone} or use the form below.`} />
    </>
  );
}
